class FakeScrollbar {
  constructor({ box, track, thumb, minThumbPx = 24 } = {}) {
    this.box = typeof box === 'string' ? document.querySelector(box) : box;
    this.track = typeof track === 'string' ? document.querySelector(track) : track;
    this.thumb = typeof thumb === 'string' ? document.querySelector(thumb) : thumb;
    this.minThumbPx = minThumbPx;
    if (!this.box || !this.track || !this.thumb) return;

    const trackCS = getComputedStyle(this.track);
    if (trackCS.position === 'static') this.track.style.position = 'relative';
    const thumbCS = getComputedStyle(this.thumb);
    if (thumbCS.position === 'static') this.thumb.style.position = 'absolute';
    this.thumb.style.top = this.thumb.style.top || '0';
    this.thumb.style.left = this.thumb.style.left || '0';
    this.thumb.style.pointerEvents = 'none';

    this.track.style.pointerEvents = 'auto';
    this.thumb.style.zIndex = '1';

    this._overlay = document.createElement('div');
    Object.assign(this._overlay.style, {
      position: 'absolute',
      inset: '0',
      background: 'transparent',
      cursor: 'grab',
      touchAction: 'none',
      pointerEvents: 'auto',
      zIndex: '2',
      display: 'block',
    });
    this.track.appendChild(this._overlay);

    this.update = this.update.bind(this);
    this._down = this._down.bind(this);
    this._move = this._move.bind(this);
    this._up = this._up.bind(this);

    this.box.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    this._overlay.addEventListener('pointerdown', this._down, { passive: false });

    this.update();
  }

  _sizes() {
    const scrollW = this.box.scrollWidth;
    const clientW = this.box.clientWidth;
    const trackW = this.track.clientWidth;
    const maxScroll = Math.max(0, scrollW - clientW);
    const ratio = clientW > 0 ? clientW / (scrollW || 1) : 0;
    const thumbW = Math.max(this.minThumbPx, Math.floor(trackW * ratio));
    const maxX = Math.max(0, trackW - thumbW);
    return { scrollW, clientW, trackW, maxScroll, thumbW, maxX };
  }

  update() {
    const { maxScroll, thumbW, maxX } = this._sizes();
    if (!this.track.clientWidth || maxScroll <= 0) {
      this.thumb.style.width = '0px';
      this.thumb.style.transform = 'translateX(0)';
      this.track.style.visibility = 'hidden';
      return;
    }
    this.track.style.visibility = '';

    const x = (this.box.scrollLeft / maxScroll) * maxX;
    this.thumb.style.width = `${thumbW}px`;
    this.thumb.style.transform = `translateX(${Math.round(x)}px)`;
  }

  _down(e) {
    e.preventDefault();
    this._overlay.setPointerCapture?.(e.pointerId);

    const { maxScroll, maxX } = this._sizes();
    const rect = this.track.getBoundingClientRect();

    this._drag = {
      id: e.pointerId,
      startX: e.clientX,
      startScroll: this.box.scrollLeft,
      maxScroll,
      maxX,
      rectLeft: rect.left,
      moved: false,
    };

    window.addEventListener('pointermove', this._move, { passive: false });
    window.addEventListener('pointerup', this._up, { passive: true });
    this._overlay.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }

  _move(e) {
    if (!this._drag) return;
    e.preventDefault();
    const dx = e.clientX - this._drag.startX;
    if (!this._drag.moved && Math.abs(dx) > 3) this._drag.moved = true;

    if (!this._drag.moved) return;

    const dScroll = this._drag.maxX > 0 ? (dx / this._drag.maxX) * this._drag.maxScroll : 0;
    const next = Math.max(0, Math.min(this._drag.startScroll + dScroll, this._drag.maxScroll));
    this.box.scrollLeft = next;
  }

  _up(e) {
    if (!this._drag) return;
    try {
      this._overlay.releasePointerCapture?.(this._drag.id);
    } catch {}

    window.removeEventListener('pointermove', this._move);
    window.removeEventListener('pointerup', this._up);
    this._overlay.style.cursor = 'grab';
    document.body.style.userSelect = '';

    if (!this._drag.moved) {
      const clickX = e.clientX - this._drag.rectLeft;
      this._jumpTo(clickX);
    }

    this._drag = null;
  }

  _jumpTo(trackX) {
    const { maxScroll, maxX, thumbW } = this._sizes();
    if (maxScroll <= 0 || maxX <= 0) return;

    const targetThumbX = Math.max(0, Math.min(trackX - thumbW / 2, maxX));
    const targetScroll = (targetThumbX / maxX) * maxScroll;

    this.box.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  destroy() {
    this.box.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.update);
    this._overlay.removeEventListener('pointerdown', this._down);
    window.removeEventListener('pointermove', this._move);
    window.removeEventListener('pointerup', this._up);
    if (this._overlay?.parentNode) this._overlay.parentNode.removeChild(this._overlay);
    document.body.style.userSelect = '';
    this._drag = null;
  }
}

class ScrollButtons {
  constructor({ box, prev, next, smooth = true } = {}) {
    this.box = typeof box === 'string' ? document.querySelector(box) : box;
    this.prev = typeof prev === 'string' ? document.querySelector(prev) : prev;
    this.next = typeof next === 'string' ? document.querySelector(next) : next;
    this.smooth = smooth;

    if (!this.box || !this.prev || !this.next) return;

    this.track = this.box.firstElementChild || this.box;

    this._onClickPrev = this._onClickPrev.bind(this);
    this._onClickNext = this._onClickNext.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);

    this.prev.addEventListener('click', this._onClickPrev);
    this.next.addEventListener('click', this._onClickNext);
    this.box.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);

    this._computeSnaps();
    this._updateButtons();
  }

  _computeSnaps() {
    const items = Array.from(this.track?.children || []);
    const rectB = this.box.getBoundingClientRect();
    this._snaps = items.map((el) => el.getBoundingClientRect().left - rectB.left + this.box.scrollLeft);
    if (!this._snaps.length) this._snaps = [0];
  }

  _nearestIndex(left) {
    let best = 0,
      bestD = Infinity;
    for (let i = 0; i < this._snaps.length; i++) {
      const d = Math.abs(this._snaps[i] - left);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  _scrollTo(left) {
    this.box.scrollTo({ left, behavior: this.smooth ? 'smooth' : 'auto' });
  }

  _updateButtons() {
    const eps = 1;
    const maxScroll = Math.max(0, this.box.scrollWidth - this.box.clientWidth);
    const L = this.box.scrollLeft;
    this.prev.disabled = L <= eps;
    this.next.disabled = L >= maxScroll - eps;
  }

  _onClickPrev(e) {
    e.preventDefault();
    this._computeSnaps();
    const i = this._nearestIndex(this.box.scrollLeft);
    this._scrollTo(this._snaps[Math.max(0, i - 1)]);
  }

  _onClickNext(e) {
    e.preventDefault();
    this._computeSnaps();
    const i = this._nearestIndex(this.box.scrollLeft);
    this._scrollTo(this._snaps[Math.min(this._snaps.length - 1, i + 1)]);
  }

  _onScroll() {
    this._updateButtons();
  }
  _onResize() {
    this._computeSnaps();
    this._updateButtons();
  }

  destroy() {
    this.prev.removeEventListener('click', this._onClickPrev);
    this.next.removeEventListener('click', this._onClickNext);
    this.box.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  }
}
